
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from './LanguageSwitcher';

interface NavbarProps {
  onContact: () => void;
}

export default function Navbar({ onContact }: NavbarProps) {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Update scroll state
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Prevent background scrolling when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const navLinks = [
    { href: '#productos', label: t('nav.productos') },
    { href: '#proceso', label: t('nav.proceso') },
    { href: '#por-que-elegirnos', label: t('nav.empresa') },
  ];

  const handleContactMobile = () => {
    setIsMobileMenuOpen(false);
    onContact();
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled || isMobileMenuOpen ? 'bg-[#03912c] shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-5">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center">
            <img
              src="https://static.readdy.ai/image/d4364c93ef6f707650170405d0b37024/20667ea90eb4947e1e8636b4d4e845c0.png"
              alt="PPP Exports"
              className="h-10 sm:h-20 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm font-medium text-white hover:text-white/80 transition-colors cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={onContact}
              className="px-6 py-2.5 rounded-full text-sm font-medium bg-[#d6df00] text-[#1A1A1A] hover:bg-[#c2cb00] transition-all hover:scale-105 cursor-pointer whitespace-nowrap"
            >
              {t('nav.contactar')}
            </button>
            <LanguageSwitcher />
          </div>

          {/* Mobile Actions */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              className="w-9 h-9 flex items-center justify-center text-white cursor-pointer"
              aria-label="Menú"
            >
              <i
                className={`${
                  isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
                } text-2xl`}
              ></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden overflow-hidden transition-all duration-300 ease-in-out ${
          isMobileMenuOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-[#03912c] border-t border-white/10 px-5 py-4 space-y-1">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-3 text-[0.95rem] font-medium text-white hover:bg-white/10 rounded-lg py-2.5 px-3 cursor-pointer transition-colors"
            >
              {link.label}
            </a>
          ))}
          <div className="pt-2">
            <button
              onClick={handleContactMobile}
              className="block w-full text-center px-6 py-2.5 rounded-full text-[0.95rem] font-medium bg-[#d6df00] text-[#1A1A1A] hover:bg-[#c2cb00] transition-all cursor-pointer whitespace-nowrap"
            >
              {t('nav.contactar')}
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
}
